import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import AccessDenied from "components/AccessDenied";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import TableSearch from "components/TableSearch";
import DataTable from "examples/Tables/DataTable";
import { useEffect, useMemo, useState } from "react";
import languagesService from "services/languages-service";
import TranslationsEditNewDialog from "./TranslationsEditNewDialog";
import { useLanguage } from "contexts/language.context";

export default function AllTranslations({ permissions }) {
  const { label } = useLanguage();

  const [translations, setTranslations] = useState(null);
  const [languages, setLanguages] = useState(null);
  const [refreshSignal, setRefreshSignal] = useState(null);
  const [editDialogIsOpen, setEditDialogIsOpen] = useState(false);

  const columns = useMemo(() => {
    if (!languages) return [];
    const columns = languages.map((language) => {
      return {
        Header: language.name,
        accessor: "" + language.id,
      };
    });
    if (permissions.update || permissions.delete) {
      columns.push({
        Header: "",
        accessor: "actions",
        align: "right",
        width: "100%",
      });
    }

    return columns;
  }, [languages]);

  async function handleGetLanguages() {
    const [response, data] = await languagesService.getMultipleLanguages({
      include: "expressions",
    });

    if (response.ok) {
      setLanguages(data);
    }
  }

  async function handleUpdateTranslation(newElement) {
    const promises = Object.entries(newElement).map(([key, value]) => {
      return languagesService.updateExpression(key, value.id, {
        content: value.content,
      });
    });

    const values = await Promise.all(promises);

    const ok = values.reduce((prev, curr) => {
      return prev && curr[0].ok;
    }, true);

    if (ok) {
      setRefreshSignal(Math.random());
      return { success: true };
    } else {
      return { error: "Something wrong happened" };
    }
  }

  const rows = useMemo(() => {
    if (!languages || !translations) return [];
    const rows = {};
    function s(v) {
      return "-" + (v?.toString() || "");
    }

    translations.forEach((translation) => {
      const key =
        translation.code +
        s(translation.accountTypeId) +
        s(translation.contactPositionId) +
        s(translation.kpiAnswerReasonId) +
        s(translation.kpiValueTypeAnswerId) +
        s(translation.kpiValueTypeId) +
        s(translation.statusId);

      if (!rows[key]) rows[key] = {};
      rows[key] = {
        ...rows[key],
        ["" + translation.languageId]: {
          content: translation.content,
          id: translation.id,
        },
      };
    });

    return Object.values(rows).map((translation) => {
      const output = {};
      languages.map((language) => {
        output["" + language.id] =
          translation["" + language.id]?.content || "----";
        output.actions = (
          <MDBox
            display="flex"
            flexDirection="row"
            justifyContent="end"
            alignItems="center"
          >
            {permissions.update && (
              <IconButton
                size="small"
                onClick={() => {
                  setEditDialogIsOpen({ ...translation });
                }}
              >
                <Icon>edit</Icon>
              </IconButton>
            )}
          </MDBox>
        );
      });

      return output;
    });
  }, [translations, languages]);

  useEffect(() => {
    handleGetLanguages();
  }, []);

  return (
    <>
      <TranslationsEditNewDialog
        type="edit"
        onClose={() => setEditDialogIsOpen(null)}
        isOpen={!!editDialogIsOpen}
        onSubmit={handleUpdateTranslation}
        data={editDialogIsOpen}
        languages={languages}
      />

      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
                display="flex"
                flexDirection="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <MDTypography variant="h6" color="white">
                  {label("translation-plural")}{" "}
                  {permissions.get ? `(${translations?.length || 0})` : ""}
                </MDTypography>
              </MDBox>
              {permissions.get ? (
                <>
                  <MDBox p={2} mt={2}>
                    <TableSearch
                      id="all-translations"
                      internalLoading={!Boolean(translations)}
                      fullWidth
                      setData={setTranslations}
                      onSearch={async (request, callback) => {
                        callback(
                          await languagesService.getMultipleExpressions(
                            "all",
                            request
                          )
                        );
                      }}
                      refreshSignal={refreshSignal}
                      label={label("search-translations-label")}
                    />
                  </MDBox>
                  <MDBox>
                    <DataTable
                      loading={!Boolean(translations)}
                      table={{ columns, rows }}
                      isSorted={false}
                      entriesPerPage={false}
                      showTotalEntries={false}
                      noEndBorder
                    />
                  </MDBox>
                </>
              ) : (
                <AccessDenied type="component" />
              )}
            </Card>
          </Grid>
        </Grid>
      </MDBox>
    </>
  );
}
